import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublishIcon from '@mui/icons-material/Publish';
import HistoryIcon from '@mui/icons-material/History';
import {
  AttorneysContent,
  CourtEmailContent,
  LetterContent,
  LetterKey,
  LettersApi,
  LettersMeta,
  LetterValidationIssue,
  TemplateKey,
  TemplateResponse,
  TemplateVersionSummary,
} from '../../api/LettersApi';
import LetterBlockEditor from './LetterBlockEditor';
import AttorneysEditor from './AttorneysEditor';
import CourtEmailEditor from './CourtEmailEditor';

/**
 * The letters of representation, editable in place (Lloyd, Aug 17 2026).
 *
 * Reading is open to any signed-in manager — staff should be able to see what goes out over the
 * firm's name. Publishing needs the letters permission, which Lloyd holds; without it this screen is
 * the same screen with the boxes disabled, rather than a screen nobody else can find. The server
 * enforces that independently, so what is hidden here is a courtesy, not the control.
 */

const isLetterKey = (key: TemplateKey): key is LetterKey =>
  key !== 'attorneys' && key !== 'court-email';

const formatWhen = (value: string | null): string => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const ManagerLettersView: React.FC = () => {
  const [meta, setMeta] = React.useState<LettersMeta | null>(null);
  const [activeKey, setActiveKey] = React.useState<TemplateKey>('not-guilty');

  const [loaded, setLoaded] = React.useState<TemplateResponse<unknown> | null>(null);
  const [draft, setDraft] = React.useState<unknown>(null);
  const [versions, setVersions] = React.useState<TemplateVersionSummary[]>([]);

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');
  const [issues, setIssues] = React.useState<LetterValidationIssue[]>([]);
  const [notice, setNotice] = React.useState<string>('');

  const [previewPdf, setPreviewPdf] = React.useState<string>('');
  const [previewEmailHtml, setPreviewEmailHtml] = React.useState<string>('');
  const [ticketNumberKnown, setTicketNumberKnown] = React.useState(true);
  const [emailHasAttachments, setEmailHasAttachments] = React.useState(true);
  const [previewing, setPreviewing] = React.useState(false);
  const [courtDateKnown, setCourtDateKnown] = React.useState(true);
  const [dscTaken, setDscTaken] = React.useState(false);

  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishNote, setPublishNote] = React.useState('');
  const [publishing, setPublishing] = React.useState(false);
  const [revertTarget, setRevertTarget] = React.useState<number | null>(null);
  const [showHistory, setShowHistory] = React.useState(false);

  const canEdit = meta?.canEdit === true;
  const dirty = React.useMemo(
    () => draft != null && loaded != null && JSON.stringify(draft) !== JSON.stringify(loaded.content),
    [draft, loaded],
  );

  const load = React.useCallback(async (key: TemplateKey) => {
    setLoading(true);
    setError('');
    setIssues([]);
    setNotice('');
    setPreviewPdf('');
    setPreviewEmailHtml('');
    try {
      const response = isLetterKey(key)
        ? await LettersApi.getLetter(key)
        : key === 'court-email'
        ? await LettersApi.getCourtEmail()
        : await LettersApi.getAttorneys();
      setLoaded(response as TemplateResponse<unknown>);
      setDraft(JSON.parse(JSON.stringify(response.content)));
      setVersions(response.versions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load this letter');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    LettersApi.meta()
      .then(setMeta)
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Could not load the letters'),
      );
  }, []);

  React.useEffect(() => {
    load(activeKey);
  }, [activeKey, load]);

  // A half-finished letter is not something to lose to a stray click on another tab.
  React.useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const switchTab = (key: TemplateKey) => {
    if (dirty && !window.confirm('You have unpublished changes here. Leave them behind?')) {
      return;
    }
    setActiveKey(key);
  };

  const check = async () => {
    try {
      const result = await LettersApi.validate(activeKey, draft);
      setIssues(result.issues);
      if (result.issues.length === 0) {
        setNotice('No problems found.');
      }
      return result.issues.length === 0;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not check this letter');
      return false;
    }
  };

  const preview = async () => {
    if (activeKey === 'attorneys') return;
    setPreviewing(true);
    setError('');
    setIssues([]);
    try {
      if (activeKey === 'court-email') {
        const result = await LettersApi.previewEmail(draft as CourtEmailContent, {
          ticketNumberKnown,
          hasNotGuiltyAttachments: emailHasAttachments,
        });
        setPreviewEmailHtml(result.html);
        return;
      }
      const result = await LettersApi.preview(activeKey, draft as LetterContent, {
        courtDateKnown,
        hasTakenDscInPastYear: dscTaken,
      });
      setPreviewPdf(`data:application/pdf;base64,${result.pdfBase64}`);
    } catch (err) {
      const withIssues = err as Error & { issues?: LetterValidationIssue[] };
      setIssues(withIssues.issues ?? []);
      setError(withIssues.message || 'The preview could not be produced');
    } finally {
      setPreviewing(false);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setError('');
    try {
      const result = await LettersApi.publish(activeKey, draft, publishNote || null);
      setVersions(result.versions);
      setPublishOpen(false);
      setPublishNote('');
      setNotice(
        `Published as version ${result.version}. Letters made from now on use this wording.`,
      );
      await load(activeKey);
    } catch (err) {
      const withIssues = err as Error & { issues?: LetterValidationIssue[] };
      setIssues(withIssues.issues ?? []);
      setError(withIssues.message || 'Could not publish');
      setPublishOpen(false);
    } finally {
      setPublishing(false);
    }
  };

  const revert = async (version: number) => {
    setError('');
    try {
      const result = await LettersApi.revert(activeKey, version);
      setVersions(result.versions);
      setNotice(`Reverted to version ${version}, published as version ${result.version}.`);
      await load(activeKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not revert');
    } finally {
      setRevertTarget(null);
    }
  };

  const viewVersion = async (version: number) => {
    try {
      const result = await LettersApi.getVersion(activeKey, version);
      setDraft(result.content);
      setNotice(
        `Showing version ${version}. Nothing has changed yet — publish to put this wording back in force.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load that version');
    }
  };

  if (!meta) {
    return (
      <>
        <Container sx={{ py: 4 }}>
          {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
        </Container>
      </>
    );
  }

  return (
    /*
     * Lloyd, Aug 19 2026: "there was no back link to take me out of it, or the menu in the upper
     * right corner." This page was the only manager page in the app that never rendered the nav, so
     * the browser back button was genuinely the only way out.
     *
     * The nav is no longer this page's business — it is rendered once by ManagerLayout, which every
     * signed-in manager route now hangs off. That is the actual fix: a page cannot forget the nav
     * when a page no longer renders it.
     */
    <>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Typography variant="h5" gutterBottom>
          Letters of representation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The wording sent to courts. Changes take effect on letters made after you publish; letters
          already sent are unchanged.
        </Typography>

        {!canEdit && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Read only</AlertTitle>
            You can read the letters and preview them. Changing them is restricted.
          </Alert>
        )}

        <Tabs
          value={activeKey}
          onChange={(_e, value) => switchTab(value as TemplateKey)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {meta.letters.map((letter) => (
            <Tab key={letter.key} value={letter.key} label={letter.label} />
          ))}
          <Tab value="court-email" label="Email to the court" />
          <Tab value="attorneys" label="Attorneys who sign" />
        </Tabs>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>
            {notice}
          </Alert>
        )}
        {issues.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>This cannot be published yet</AlertTitle>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {issues.map((issue, index) => (
                <li key={index}>
                  <strong>{issue.where}:</strong> {issue.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {loading || !loaded || draft == null ? (
          <CircularProgress />
        ) : (
          <>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ md: 'center' }}
                justifyContent="space-between"
              >
                <Box>
                  {loaded.isDefault ? (
                    <Chip size="small" label="Original wording — never edited" />
                  ) : (
                    <Typography variant="body2">
                      <strong>Version {loaded.version}</strong> in force
                      {loaded.publishedBy ? `, published by ${loaded.publishedBy}` : ''}
                      {loaded.publishedAt ? ` on ${formatWhen(loaded.publishedAt)}` : ''}
                    </Typography>
                  )}
                  {dirty && (
                    <Chip size="small" color="warning" sx={{ mt: 1 }} label="Unpublished changes" />
                  )}
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button startIcon={<HistoryIcon />} onClick={() => setShowHistory((v) => !v)}>
                    History ({versions.length})
                  </Button>
                  <Button onClick={check}>Check</Button>
                  {activeKey !== 'attorneys' && (
                    <Button
                      startIcon={<VisibilityIcon />}
                      onClick={preview}
                      disabled={previewing}
                    >
                      {previewing ? 'Rendering…' : isLetterKey(activeKey) ? 'Preview PDF' : 'Preview email'}
                    </Button>
                  )}
                  {canEdit && (
                    <>
                      <Button
                        disabled={!dirty}
                        onClick={() => setDraft(JSON.parse(JSON.stringify(loaded.content)))}
                      >
                        Discard changes
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<PublishIcon />}
                        disabled={!dirty}
                        onClick={() => setPublishOpen(true)}
                      >
                        Publish
                      </Button>
                    </>
                  )}
                </Stack>
              </Stack>

              {showHistory && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  {versions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      This has never been edited, so there is nothing to go back to.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Version</TableCell>
                          <TableCell>Published</TableCell>
                          <TableCell>By</TableCell>
                          <TableCell>Note</TableCell>
                          <TableCell align="right" />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {versions.map((version) => (
                          <TableRow key={version.version}>
                            <TableCell>
                              {version.version}
                              {version.version === loaded.version && (
                                <Chip size="small" label="in force" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{formatWhen(version.publishedAt)}</TableCell>
                            <TableCell>{version.publishedBy ?? '—'}</TableCell>
                            <TableCell>{version.note ?? '—'}</TableCell>
                            <TableCell align="right">
                              <Button size="small" onClick={() => viewVersion(version.version)}>
                                View
                              </Button>
                              {canEdit && version.version !== loaded.version && (
                                <Button
                                  size="small"
                                  onClick={() => setRevertTarget(version.version)}
                                >
                                  Revert to this
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </Paper>

            {isLetterKey(activeKey) ? (
              <LetterBlockEditor
                content={draft as LetterContent}
                placeholders={meta.placeholders}
                conditions={meta.conditions}
                readOnly={!canEdit}
                onChange={setDraft}
              />
            ) : activeKey === 'court-email' ? (
              <CourtEmailEditor
                content={draft as CourtEmailContent}
                meta={meta}
                readOnly={!canEdit}
                onChange={setDraft}
              />
            ) : (
              <AttorneysEditor
                content={draft as AttorneysContent}
                readOnly={!canEdit}
                onChange={setDraft}
              />
            )}

            {isLetterKey(activeKey) && (
              <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  An example case, rendered exactly as a court receives it — same letterhead, same
                  PDF. Use the switches to see the parts that only appear on some cases.
                </Typography>
                <Stack direction="row" spacing={2} sx={{ my: 1 }} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={courtDateKnown}
                        onChange={(e) => setCourtDateKnown(e.target.checked)}
                      />
                    }
                    label="Court date known"
                  />
                  {activeKey === 'dsc-under-25' && (
                    <FormControlLabel
                      control={
                        <Switch checked={dscTaken} onChange={(e) => setDscTaken(e.target.checked)} />
                      }
                      label="Client has taken a DSC this year"
                    />
                  )}
                  <Button onClick={preview} disabled={previewing}>
                    {previewing ? 'Rendering…' : 'Refresh preview'}
                  </Button>
                </Stack>
                {previewPdf ? (
                  <object
                    data={previewPdf}
                    type="application/pdf"
                    width="100%"
                    height="800"
                    aria-label="Letter preview"
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Press “Preview PDF” to see this wording as a letter.
                  </Typography>
                )}
              </Paper>
            )}

            {activeKey === 'court-email' && (
              <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Preview
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The email as a clerk receives it, on an example case. The switches show the parts
                  that only appear on some cases.
                </Typography>
                <Stack direction="row" spacing={2} sx={{ my: 1 }} flexWrap="wrap">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ticketNumberKnown}
                        onChange={(e) => setTicketNumberKnown(e.target.checked)}
                      />
                    }
                    label="Ticket number known"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={emailHasAttachments}
                        onChange={(e) => setEmailHasAttachments(e.target.checked)}
                      />
                    }
                    label="Not-guilty case (39.14 + preservation attached)"
                  />
                  <Button onClick={preview} disabled={previewing}>
                    {previewing ? 'Rendering…' : 'Refresh preview'}
                  </Button>
                </Stack>
                {previewEmailHtml ? (
                  // srcDoc with no sandbox permissions: this is rendered email HTML, and the frame
                  // should display it, not run anything from it.
                  <Box
                    component="iframe"
                    title="Email preview"
                    srcDoc={previewEmailHtml}
                    sandbox=""
                    sx={{ width: '100%', height: 700, border: '1px solid', borderColor: 'divider' }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Press “Preview email” to see this wording as an email.
                  </Typography>
                )}
              </Paper>
            )}
          </>
        )}

        <Dialog open={publishOpen} onClose={() => setPublishOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>Publish this wording?</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Every letter of this type made from now on uses this wording. Letters already sent are
              not affected, and the version in force now is kept so you can go back to it.
            </DialogContentText>
            <TextField
              fullWidth
              label="What changed (optional)"
              placeholder="e.g. Added Austin to the signature block"
              value={publishNote}
              onChange={(e) => setPublishNote(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPublishOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={publish} disabled={publishing}>
              {publishing ? 'Publishing…' : 'Publish'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={revertTarget != null} onClose={() => setRevertTarget(null)}>
          <DialogTitle>Go back to version {revertTarget}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This publishes version {revertTarget}'s wording again as a new version. Nothing is
              deleted — the history stays as it is.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevertTarget(null)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => revertTarget != null && revert(revertTarget)}
            >
              Revert
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default ManagerLettersView;
